import { useEffect, useState } from 'react'
import QRCode from 'qrcode'
import { QrCode } from 'lucide-react'
import { transferPlanUrl } from '@/lib/transferQr'

/**
 * The plan's QR code, for a handset to open it from.
 *
 * Rendered as an SVG STRING rather than a canvas: it stays crisp when the page
 * is printed, which is how this reaches a freezer door in a room where nobody
 * carries the monitor.
 *
 * Only on an APPROVED plan. A draft is still being edited, so a code pointing
 * at one invites somebody to walk to a freezer with a plan that may change
 * under them before they get there.
 *
 * `qrcode` is imported here and nowhere under `src/mobile/**`: the handset
 * needs the DECODER it already has, and its 250 KB budget must not pay for a
 * generator it never calls.
 */
export function TransferPlanQr({ transferId }: { transferId: string }) {
  // `window.location.origin` rather than a configured host: the code has to
  // resolve from the same deployment the person is looking at, and a baked-in
  // host would send a staging QR at production.
  const url = transferPlanUrl(window.location.origin, transferId)
  const { svg, error } = useQrSvg(url)

  return (
    <div className='flex items-start gap-4 rounded-lg border p-4'>
      <div className='shrink-0'>
        <QrImage svg={svg} label='QR code to open this plan on a handset' />
      </div>

      <div className='min-w-0 space-y-1 text-sm'>
        <p className='font-medium'>Scan to open this plan on a handset</p>
        <p className='text-[13px] text-muted-foreground'>
          Point a phone camera at the code. It opens the plan on the mobile surface, where the
          moves can be worked through one at a time.
        </p>
        {error ? (
          <p className='text-[13px] text-destructive'>Could not draw the code: {error}</p>
        ) : (
          <p className='break-all font-mono text-[11px] text-muted-foreground'>{url}</p>
        )}
      </div>
    </div>
  )
}

/**
 * The library's own SVG, sized by the wrapper rather than by its attributes,
 * so print and screen both get a sharp code. A placeholder while it draws.
 */
export function QrImage({
  svg,
  label,
  className = 'h-32 w-32',
}: {
  svg: string | null
  label: string
  className?: string
}) {
  return svg ? (
    <div
      className={`${className} [&>svg]:h-full [&>svg]:w-full`}
      aria-label={label}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  ) : (
    <div className={`${className} flex items-center justify-center rounded border bg-muted/40`}>
      <QrCode className='h-8 w-8 text-muted-foreground' />
    </div>
  )
}

/** A URL drawn as a QR code, as an SVG string. Shared by the plan and move codes. */
export function useQrSvg(url: string): { svg: string | null; error: string | null } {
  const [svg, setSvg] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    QRCode.toString(url, {
      type: 'svg',
      margin: 1,
      // Q over the default M: this gets photographed on a phone, in a room
      // with gloves and condensation, and often off a screen at an angle.
      errorCorrectionLevel: 'Q',
    })
      .then((out) => {
        if (live) setSvg(out)
      })
      .catch((e: unknown) => {
        if (live) setError(e instanceof Error ? e.message : 'Could not draw the code')
      })
    return () => {
      live = false
    }
  }, [url])

  return { svg, error }
}
